import { SUPABASE_PUBLISHABLE_KEY } from '../../lib/supabase';
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchAllRows, fetchAllRowsResult } from '../../lib/fetchAll';
import InfoTip from '../components/InfoTip';
import { fetchWithTimeout } from '../../lib/bookSearch';
import { logAudit } from '../lib/auditLog';
import { loadDuplicatePairs } from '../lib/adminCounts';

// Scanning the pending queue is a batch job and can legitimately run long, so
// it gets a generous deadline rather than the search box's. It still needs one:
// the button shows "Scanning..." until this settles, and without a deadline
// that is forever.
const SCAN_TIMEOUT_MS = 60000;

// One batch per press. The sweep is O(n) rather than pairwise, but it still
// generates an embedding for anything missing one, so a batch is bounded to
// keep a single press comfortably inside SCAN_TIMEOUT_MS. Press again to take
// the next batch.
const SWEEP_BATCH = 25;

// ── Helpers ───────────────────────────────────────────────────
function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── Shared UI ─────────────────────────────────────────────────
function Pill({ children, color = 'gray' }) {
  const styles = {
    gray:  { background: 'var(--brand-cream)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' },
    merge: { background: '#fdf4ff', color: '#701a75', border: '1px solid #f0abfc' },
    blue:  { background: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe' },
    green: { background: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' },
    red:   { background: 'var(--feedback-error-bg)', color: '#991b1b', border: '1px solid #fca5a5' },
    amber: { background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)' },
    purple:{ background: '#fdf4ff', color: '#6b21a8', border: '1px solid #d8b4fe' },
    // point of view and setting. A colour named here and nowhere else renders
    // as an unstyled pill — `...styles[color]` spreads undefined silently — so
    // a new CATEGORY_LABELS colour needs an entry here in the same change.
    teal:  { background: '#ccfbf1', color: '#115e59', border: '1px solid #99f6e4' },
  };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', fontSize: 'var(--font-size-xxs)',
      fontWeight: 700, padding: '3px 10px', borderRadius: 'var(--radius-xxl)',
      letterSpacing: 0.2, whiteSpace: 'nowrap', flexShrink: 0,
      ...styles[color],
    }}>
      {children}
    </span>
  );
}

function SimBar({ score }) {
  // No score is not a zero score. These rows were flagged by an earlier process
  // that never wrote a `trope_merge_log` row, so there is nothing to read — and
  // printing "0%" next to a merge suggestion says the two names have nothing in
  // common, which is the opposite of why it is sitting in the queue.
  if (score === null || score === undefined) {
    return (
      <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--neutral-400)', flexShrink: 0, whiteSpace: 'nowrap' }}>
        no score recorded
      </span>
    );
  }
  const pct = Math.round(score * 100);
  const color = pct >= 90 ? '#16a34a' : pct >= 85 ? '#9333ea' : '#d97706';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', flexShrink: 0 }}>
      <div style={{ width: 56, height: 4, background: 'var(--brand-sand)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2 }} />
      </div>
      <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color, width: 28, textAlign: 'right' }}>
        {pct}%
      </span>
    </div>
  );
}

function Btn({ children, onClick, variant = 'default', disabled, small }) {
  const variants = {
    default: { background: 'var(--brand-white)',    color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' },
    approve: { background: 'var(--feedback-success-bg)', color: '#065f46', border: '1px solid #86efac' },
    reject:  { background: '#fff8f8', color: '#991b1b', border: '1px solid #fca5a5' },
    merge:   { background: '#faf5ff', color: '#6b21a8', border: '1px solid #d8b4fe' },
    primary: { background: 'var(--neutral-800)', color: 'var(--brand-white)',    border: '1px solid var(--neutral-800)' },
    amber:   { background: '#fffbeb', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)' },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      fontSize: small ? 11 : 11.5, fontWeight: 600,
      padding: small ? '4px 10px' : '6px 13px',
      borderRadius: 'var(--radius-md)', cursor: disabled ? 'not-allowed' : 'pointer',
      fontFamily: 'var(--font-body)', whiteSpace: 'nowrap',
      opacity: disabled ? 0.5 : 1, ...variants[variant],
    }}>
      {children}
    </button>
  );
}

// What each row in the queue actually IS.
//
// The queue has always loaded every pending row in `tropes` with no category
// filter, so it has always contained moods, genres, spice, content warnings and
// representation alongside tropes. The label did not say so: anything that was
// not `genre` was printed as "new trope". An admin approving "Angsty" was
// approving something the screen had told them was a trope.
//
// Kept in step with CATEGORY_GROUPS in supabase/functions/sweep-tropes/index.ts,
// which decides what may merge with what. If a category is added there it needs
// a label here, or it falls through to the honest default below rather than
// being mislabelled.
const CATEGORY_LABELS = {
  'trope':                   { label: 'trope',           color: 'gray'   },
  'Relationship Dynamics':   { label: 'trope · dynamics',color: 'gray'   },
  'Characters & Situations': { label: 'trope · characters', color: 'gray' },
  'Plot Devices':            { label: 'trope · plot',    color: 'gray'   },
  'Fantasy Elements':        { label: 'trope · fantasy', color: 'gray'   },
  'Representation':          { label: 'representation',  color: 'purple' },
  'genre':                   { label: 'genre',           color: 'blue'   },
  'tone':                    { label: 'mood',            color: 'amber'  },
  'Tone & Heat':             { label: 'mood',            color: 'amber'  },
  'spice':                   { label: 'spice',           color: 'red'    },
  'trigger':                 { label: 'content warning', color: 'red'    },
  'pov':                     { label: 'point of view',   color: 'teal'   },
  'setting':                 { label: 'setting',         color: 'teal'   },
};

// An unknown category says so rather than claiming to be a trope. Somebody
// approving a row needs to know when the screen does not recognise it.
function categoryPill(category) {
  return CATEGORY_LABELS[category] || { label: category ? `? ${category}` : '? no category', color: 'amber' };
}

// ══════════════════════════════════════════════════════════════
// TAB 1 — PENDING QUEUE
// ══════════════════════════════════════════════════════════════
function PendingQueue({ onCountChange, isMobile }) {
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState(null);
  const [working, setWorking]       = useState(null);
  const [scanning, setScanning]     = useState(false);
  const [scanResult, setScanResult] = useState(null);
  // 15. Which proposed merges are ticked. Seeded to all-on after a dry run,
  // so the default is still 'apply what it found' — untick to drop one.
  const [picked, setPicked] = useState(new Set());
  const [swappedItems, setSwappedItems] = useState(new Set());
  // Which kind of thing to show. The queue mixes tropes, genres, moods, spice,
  // content warnings and representation, and working a mixed pile is how a mood
  // gets approved as a trope. The Duplicates tab has had this for a while; the
  // queue that actually approves things did not.
  const [kind, setKind] = useState('all');
  const onCountChangeRef = useRef(onCountChange);
  useEffect(() => { onCountChangeRef.current = onCountChange; }, [onCountChange]);

  const load = useCallback(async () => {
    setLoading(true);
    // Paged: the queue badge in the sidebar is this array's length, so a bare
    // select would cap the badge at 1,000 as well as the queue itself.
    const pendingRead = await fetchAllRowsResult(() => supabase
      .from('tropes')
      .select('*')
      .eq('is_active', false)
      .in('merge_status', ['active', 'pending_review']));
    // A refused read is not an empty queue: say so, and do not report a count.
    if (!pendingRead.ok) {
      setLoadError(pendingRead.error || 'Could not load the queue.');
      setItems([]); setLoading(false);
      return;
    }
    setLoadError(null);
    const rawItems = pendingRead.rows;
    // Merge decisions first, then oldest.
    //
    // Only a trope with merge_status 'pending_review' AND a suggested match
    // renders Merge / Keep both; everything else is a plain Approve / Reject.
    // Sorted purely by date those merge items scatter through the whole queue —
    // with 432 pending and ~32 mergeable, the first screen was all "new trope"
    // and the merge UI looked like it did not exist. It did; it was on page 14.
    rawItems.sort((a, b) => {
      const aMerge = a.merge_status === 'pending_review' ? 0 : 1;
      const bMerge = b.merge_status === 'pending_review' ? 0 : 1;
      if (aMerge !== bMerge) return aMerge - bMerge;
      return new Date(a.created_at) - new Date(b.created_at);
    });

    const enriched = await Promise.all((rawItems || []).map(async (item) => {
      let suggested_trope = null;
      // null means "nobody wrote a score down", which is NOT zero. Defaulting it
      // to 0 printed "0%" against three rows flagged by an older process that
      // never logged one, and 0% reads as "these two names are unrelated".
      let similarity = null;
      if (item.merge_status === 'pending_review' && item.merged_into) {
        const [{ data: target }, { data: log }] = await Promise.all([
          supabase.from('tropes').select('id, canonical_name, usage_count').eq('id', item.merged_into).single(),
          // maybeSingle, not single: no log row is the ordinary case here, and
          // single() turns it into an error that nothing reads.
          // The score is the duplicate check's, so skip the two spelling
          // actions (spelling_corrected, spelling_suggested). process-trope can
          // write one for a waiting tag after its flag, always at 88% or more,
          // and without this the card shows that score instead and saves it to
          // the merge history. Not .eq('action', 'flagged_review'): Keep both
          // relabels the row kept_both and the card stays on the Queue tab.
          supabase.from('trope_merge_log').select('similarity_score').eq('original_trope_id', item.id)
            .not('action', 'in', '(spelling_corrected,spelling_suggested)')
            .order('created_at', { ascending: false }).limit(1).maybeSingle(),
        ]);
        suggested_trope = target || null;
        similarity = log?.similarity_score ?? null;
      }
      const { count: bookCount } = await supabase
        .from('book_tags').select('*', { count: 'exact', head: true }).eq('trope_id', item.id);
      return { ...item, suggested_trope, similarity, affected_book_count: bookCount || 0 };
    }));

    setItems(enriched);
    // Two numbers, because they answer different questions: how many rows are
    // waiting, and how many of those are a merge decision rather than a plain
    // approve/reject. The header tooltip states both so the "32 merge
    // suggestions" on the Tropes page cannot read as a rival queue.
    const mergeable = enriched.filter(i => i.merge_status === 'pending_review' && i.suggested_trope).length;
    onCountChangeRef.current?.(enriched.length, mergeable);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleApprove(item) {
    setWorking(item.id);
    const { error } = await supabase
      .from('tropes').update({ is_active: true, merge_status: 'active' }).eq('id', item.id);
    if (error) { alert('Error: ' + error.message); setWorking(null); return; }
    await logAudit('create', 'trope', `Approved "${item.canonical_name}" as new canonical`, item.id);
    load(); setWorking(null);
  }

  async function handleReject(item) {
    setWorking(item.id);
    const { error } = await supabase
      .from('tropes').update({ is_active: false, merge_status: 'rejected' }).eq('id', item.id);
    if (error) { alert('Reject failed: ' + error.message); setWorking(null); return; }
    await logAudit('delete', 'trope', `Rejected "${item.canonical_name}"`, item.id);
    load(); setWorking(null);
  }

  async function handleMerge(item, isSwapped) {
    setWorking(item.id);
    const keepId   = isSwapped ? item.id           : item.merged_into;
    const aliasId  = isSwapped ? item.merged_into  : item.id;
    const keepName  = isSwapped ? item.canonical_name : (item.suggested_trope?.canonical_name || '');
    const aliasName = isSwapped ? (item.suggested_trope?.canonical_name || '') : item.canonical_name;
    if (!keepId || !aliasId) { alert('No merge target'); setWorking(null); return; }
    try {
      const { data: pairNow, error: pairErr } = await supabase.from('tropes')
        .select('id, canonical_name, merge_status').in('id', [keepId, aliasId]);
      if (pairErr) { alert(`Merge failed, nothing was changed: ${pairErr.message}`); return; }
      const gone = (pairNow || []).find(t => t.merge_status === 'merged' || t.merge_status === 'rejected');
      if (gone || (pairNow || []).length !== 2) {
        alert(`Nothing was merged: "${gone?.canonical_name ?? 'one of these tags'}" was ${gone?.merge_status ?? 'deleted'} after this was flagged.`);
        return;
      }
      // One transaction on the server rather than four writes from here.
      //
      // The old version ran `update book_tags set trope_id = keep where
      // trope_id = alias` as a single statement. book_tags has a UNIQUE
      // (book_id, trope_id), so a book already carrying both tropes made that
      // update collide — and Postgres fails the whole statement, so books with
      // no conflict did not move either. It then marked the trope merged and
      // inactive on the next line, because none of these writes destructured
      // `error` and the Supabase client returns errors rather than throwing.
      // The merge reported success while every book kept a dead tag.
      const { data: result, error } = await supabase.rpc('merge_tropes', {
        p_alias_id:   aliasId,
        p_keep_id:    keepId,
        p_alias_name: aliasName,
        p_keep_name:  keepName,
        // trope_merge_log.similarity_score is NOT NULL, so an unknown score has
        // to land as 0 here. A 0 in the merge history means "not recorded",
        // not "measured at zero" — do not read it as a measurement.
        p_similarity: item.similarity || 0,
      });
      if (error) { alert(`Merge failed, nothing was changed: ${error.message}`); return; }

      // merge_tropes only retires the merged-away tag, so switch the kept one on.
      const { error: keepErr } = await supabase.from('tropes')
        .update({ is_active: true, merge_status: 'active', merged_into: null })
        .eq('id', keepId);
      // One update per direction, so a swapped merge is covered.
      const closedAt = new Date().toISOString();
      const { error: closeErrAB } = await supabase.from('trope_merge_log')
        .update({ reviewed_at: closedAt })
        .eq('original_trope_id', keepId).eq('merged_into_id', aliasId)
        .eq('action', 'flagged_review').is('reviewed_at', null);
      const { error: closeErrBA } = await supabase.from('trope_merge_log')
        .update({ reviewed_at: closedAt })
        .eq('original_trope_id', aliasId).eq('merged_into_id', keepId)
        .eq('action', 'flagged_review').is('reviewed_at', null);
      const unfinished = [
        keepErr && `"${keepName}" could not be switched on: ${keepErr.message}`,
        (closeErrAB || closeErrBA) && `the flagged pair could not be closed: ${(closeErrAB || closeErrBA).message}`,
      ].filter(Boolean);
      if (unfinished.length) alert(`Tags merged, but ${unfinished.join('; and ')}`);

      await logAudit(
        'merge', 'trope',
        `Merged "${aliasName}" → "${keepName}" (${result?.moved ?? 0} moved, ${result?.deduped ?? 0} already tagged)`,
        aliasId,
      );
      load();
    } catch (err) { alert('Merge failed: ' + err.message); }
    finally { setWorking(null); }
  }

  async function handleKeepBoth(item) {
    setWorking(item.id);
    try {
      // Both writes were unchecked. The Supabase client returns errors rather
      // than throwing, so the try/catch never fired and "Kept both" reported
      // success whether or not either write landed.
      const { error: keepErr } = await supabase.from('tropes')
        .update({ is_active: true, merge_status: 'active', merged_into: null })
        .eq('id', item.id);
      if (keepErr) throw new Error(keepErr.message);

      // Relabel the pair's open rows; insert a kept_both row only if none were touched.
      const otherId = item.suggested_trope?.id || item.merged_into;
      const relabelledAt = new Date().toISOString();
      const { data: relabelledAB, error: relabelErrAB } = await supabase.from('trope_merge_log')
        .update({ reviewed_at: relabelledAt, action: 'kept_both' })
        .eq('original_trope_id', item.id).eq('merged_into_id', otherId)
        .eq('action', 'flagged_review').is('reviewed_at', null)
        .select('id');
      if (relabelErrAB) throw new Error(`trope kept, but the decision was not logged: ${relabelErrAB.message}`);
      const { data: relabelledBA, error: relabelErrBA } = await supabase.from('trope_merge_log')
        .update({ reviewed_at: relabelledAt, action: 'kept_both' })
        .eq('original_trope_id', otherId).eq('merged_into_id', item.id)
        .eq('action', 'flagged_review').is('reviewed_at', null)
        .select('id');
      if (relabelErrBA) throw new Error(`trope kept, but the decision was not logged: ${relabelErrBA.message}`);

      if (!relabelledAB?.length && !relabelledBA?.length) {
        const { error: logErr } = await supabase.from('trope_merge_log').insert({
          original_trope_id: item.id,
          original_name:     item.canonical_name,
          merged_into_id:    item.suggested_trope?.id || item.merged_into,
          merged_into_name:  item.suggested_trope?.canonical_name || '',
          similarity_score:  item.similarity || 0,   // NOT NULL: 0 here means "not recorded"
          action:            'kept_both',
          reviewed_at:       new Date().toISOString(),
        });
        if (logErr) throw new Error(`trope kept, but the decision was not logged: ${logErr.message}`);
      }
      await logAudit('create', 'trope',
        `Kept both: "${item.canonical_name}" alongside "${item.suggested_trope?.canonical_name}"`, item.id);
      load();
    } catch (err) { alert('Keep both failed: ' + err.message); }
    finally { setWorking(null); }
  }

  // Two steps on purpose: scan tells you what it WOULD do, apply does it.
  //
  // This used to call process-trope's scan_pending, which compares every
  // pending trope against every other one — 79,800 pairs at 400 pending, with
  // a gpt-4o-mini call on the borderline ones. About an hour of work behind a
  // 60s deadline, so the button only ever reported "Fetch is aborted". It also
  // never merged anything; it only flagged, which is why this queue became a
  // manual backlog.
  //
  // sweep-tropes asks the question that scales instead: for each pending
  // trope, is there already a LIVE canonical trope that means the same thing?
  // One vector search each rather than a pairwise sweep.
  // One call to sweep-tropes. Returns a discriminated result so a caller can
  // tell "the sweep ran and decided nothing" from "the sweep never ran" — the
  // two look identical in a bare null, and confusing them is how a total
  // outage comes to read as a clean queue.
  async function sweepOnce(dryRun, only = null) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetchWithTimeout(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sweep-tropes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': SUPABASE_PUBLISHABLE_KEY,
          },
          // all:true asks the function to keep pulling batches until the
          // backlog is empty or its own time budget (RUN_BUDGET_MS, 45s) runs
          // out — which is why SCAN_TIMEOUT_MS above must stay comfortably
          // larger than that budget.
          //
          // Not sent when applying a picked set. `only` names specific ids, and
          // the rows it does not name are left unstamped, so the function's
          // "has the queue moved" test would see the same top row and stop. The
          // picked set is looped here instead, a batch at a time.
          body: JSON.stringify({ limit: SWEEP_BATCH, dryRun, ...(only ? { only } : { all: true }) }),
        },
        SCAN_TIMEOUT_MS
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        return { ok: false, reason: data.reason || `HTTP ${res.status}` };
      }
      return { ok: true, data };
    } catch (err) {
      return { ok: false, reason: err.name === 'AbortError' ? `timed out after ${SCAN_TIMEOUT_MS / 1000}s` : err.message };
    }
  }

  async function runSweep(dryRun, only = null) {
    setScanning(true);
    if (dryRun) setScanResult(null);

    const result = await sweepOnce(dryRun, only);
    if (!result.ok) {
      alert(`Scan failed: ${result.reason}`);
      setScanning(false);
      return;
    }

    setScanResult({ ...result.data, dryRun });
    if (dryRun) {
      setPicked(new Set((result.data.decisions || [])
        .filter(d => d.verdict === 'merged').map(d => d.id)));
    }
    if (!dryRun) load();
    setScanning(false);
  }

  async function handleApplySweep() {
    const ids = [...picked];
    if (!ids.length) return;
    if (!confirm(
      `Merge ${ids.length} trope${ids.length === 1 ? '' : 's'} into their matches?\n\n` +
      `Each one moves its book tags across and leaves an alias behind, so the ` +
      `old name still finds the right trope.\n\n` +
      `Anything you unticked is left exactly as it is and will come back next scan.`
    )) return;

    // The dry run now previews the whole backlog, so the picked set can be far
    // larger than one batch. Send it a batch at a time and add the results up,
    // rather than silently applying only the first 25.
    //
    // Sequential on purpose: every iteration writes to the same two tables, and
    // a merge that half-succeeds is the one outcome nobody can unpick by hand.
    setScanning(true);
    const totals = { scanned: 0, merged: 0, flagged: 0, kept: 0, failed: 0, skipped: 0, decisions: [] };
    let stoppedEarly = null;
    // Carried from the LAST successful batch, because that is the only one that
    // counted the queue after all the merges landed. Without it the summary
    // line has no `remaining` at all and silently renders nothing — so applying
    // 5 merges told the operator how many merged and never how many were left.
    //
    // `stoppedBecause` is deliberately NOT carried: it describes why one batch
    // ended, which says nothing about a set that was chunked here.
    let remaining = null, remainingCountFailed = false;

    for (let i = 0; i < ids.length; i += SWEEP_BATCH) {
      const chunk = ids.slice(i, i + SWEEP_BATCH);
      const batch = await sweepOnce(false, chunk);

      if (!batch.ok) {
        // Stop here rather than pressing on. Whatever already merged is real and
        // is reported below; continuing after an unexplained failure risks
        // repeating it for every remaining batch.
        stoppedEarly = batch.reason;
        break;
      }

      totals.scanned += batch.data.scanned || 0;
      totals.merged  += batch.data.merged  || 0;
      totals.flagged += batch.data.flagged || 0;
      totals.kept    += batch.data.kept    || 0;
      totals.failed  += batch.data.failed  || 0;
      totals.skipped += batch.data.skipped || 0;
      totals.decisions.push(...(batch.data.decisions || []));
      remaining = batch.data.remainingCountFailed ? null : (batch.data.remaining ?? null);
      remainingCountFailed = !!batch.data.remainingCountFailed;
    }

    setScanResult({ ...totals, remaining, remainingCountFailed, dryRun: false, stoppedEarly });
    setScanning(false);
    if (stoppedEarly) {
      alert(
        `Stopped partway: ${stoppedEarly}\n\n` +
        `${totals.merged} merge${totals.merged === 1 ? '' : 's'} completed and are saved. ` +
        `The rest were not attempted, scan again to pick up where this left off.`
      );
    }
    load();
  }

  // Counts come from the full list, not the filtered one, so a chip showing
  // "moods 12" still reads 12 while you are looking at genres.
  const kindCounts = items.reduce((acc, it) => {
    const k = categoryPill(it.category).label;
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const visibleItems = kind === 'all' ? items : items.filter(it => categoryPill(it.category).label === kind);

  if (loading) return <div style={{ padding: 'var(--space-xxl)', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading queue...</div>;
  if (loadError) return (
    <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--feedback-error)', fontSize: 'var(--font-size-base)', fontWeight: 600 }}>
      Could not load the queue, this is a failed read, not an empty queue. {loadError}
    </div>
  );
  if (!items.length) return (
    <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>
      ✓ Queue is clear, nothing pending
    </div>
  );

  return (
    <div>
      {/* Scan bar */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-14)', gap: 'var(--space-10)',
        alignItems: 'center', flexWrap: 'wrap',
      }}>
        {scanResult && (
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)' }}>
            {/* "Checked 270" next to a "Pending (383)" tab invites the obvious
                question and answered none of it. The scan skips rows it has
                already looked at, rows already flagged for review, and
                categories it does not cover — so the two numbers are SUPPOSED
                to differ, and saying so costs six words. */}
            {scanResult.dryRun ? 'Checked' : 'Swept'} {scanResult.scanned}
            {scanResult.dryRun && items.length > scanResult.scanned && (
              <span title="The scan skips rows it checked in an earlier run, rows already flagged for review, and categories it does not sweep.">
                {' '}of {items.length} waiting
              </span>
            )} ·{' '}
            <strong style={{ color: 'var(--neutral-800)' }}>{scanResult.merged}</strong>{' '}
            {scanResult.dryRun ? 'would merge' : 'merged'} ·{' '}
            {scanResult.flagged} to review · {scanResult.kept} left alone
            {scanResult.failed > 0 && (
              <> · <strong style={{ color: 'var(--feedback-error)' }}>{scanResult.failed} failed</strong></>
            )}
            {/* A preview and a real run need different closing sentences, and
                the preview one has to name the NEXT ACTION.

                This said "nothing changed — this was a preview" while the
                stoppedBecause span below said "checked every row waiting —
                nothing was changed". Two sentences, same meaning, side by side,
                and between them they never mentioned the button sitting right
                there. The operator scanned twice, saw the identical five rows,
                and reasonably concluded the thing was broken. It was not: a
                preview changes nothing by design, so the same five come back
                until Merge is pressed. Say that, once. */}
            {scanResult.dryRun ? (
              <> · <strong style={{ color: 'var(--neutral-800)' }}>
                {scanResult.merged > 0
                  ? `nothing merged yet, press "Merge these ${scanResult.merged}" to apply`
                  : 'nothing changed, and nothing here needs merging'}
              </strong></>
            ) : scanResult.remainingCountFailed ? (
              <> · <strong style={{ color: 'var(--feedback-error)' }}>could not count what is left</strong></>
            ) : scanResult.remaining > 0 ? (
              <> · <strong style={{ color: 'var(--neutral-800)' }}>{scanResult.remaining} still to check</strong></>
            ) : scanResult.remaining === 0 ? (
              <> · <strong style={{ color: 'var(--brand-forest)' }}>backlog clear</strong></>
            ) : null}
          </span>
        )}
        {/* Real runs only. On a preview this duplicated the sentence above
            word for word — see the comment there. */}
        {!scanResult?.dryRun && scanResult?.stoppedBecause && scanResult.remaining > 0 && (
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', fontStyle: 'italic' }}>
            {scanResult.stoppedBecause}
          </span>
        )}
        <button
          onClick={() => runSweep(true)} disabled={scanning}
          style={{
            fontSize: 'var(--font-size-sm)', fontWeight: 600, padding: '7px 14px',
            borderRadius: 'var(--radius-md)', cursor: scanning ? 'wait' : 'pointer',
            border: '1px solid #d8b4fe', background: '#faf5ff', color: '#6b21a8',
            fontFamily: 'var(--font-body)', opacity: scanning ? 0.6 : 1,
          }}
        >
          {scanning ? 'Working...' : 'Scan for duplicates'}
        </button>
        {/* Only after a dry run, and only if it actually found something. */}
        {scanResult?.dryRun && picked.size > 0 && (
          <button
            onClick={handleApplySweep} disabled={scanning}
            style={{
              fontSize: 'var(--font-size-sm)', fontWeight: 700, padding: '7px 14px',
              borderRadius: 'var(--radius-md)', cursor: scanning ? 'wait' : 'pointer',
              border: 'none', background: 'var(--brand-forest)', color: 'var(--brand-white)',
              fontFamily: 'var(--font-body)', opacity: scanning ? 0.6 : 1,
            }}
          >
            Merge these {picked.size}
          </button>
        )}
      </div>

      {/* What it would do, before it does it. A count alone is not reviewable. */}
      {scanResult?.decisions?.length > 0 && (
        <div style={{
          border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)',
          background: 'var(--brand-paper)', padding: '12px 14px', marginBottom: 'var(--space-14)',
          maxHeight: 280, overflowY: 'auto',
        }}>
          {scanResult.decisions.filter(d => d.verdict === 'merged').map(d => (
            <label key={`m${d.id}`} style={{
              display: 'flex', alignItems: 'baseline', gap: 'var(--space-sm)',
              fontSize: 12.5, marginBottom: 5, color: 'var(--neutral-600)',
              cursor: scanResult.dryRun ? 'pointer' : 'default',
            }}>
              {/* 15. Only offered on a dry run — after the merges have run there
                  is nothing left to choose. */}
              {scanResult.dryRun && (
                <input
                  type="checkbox"
                  checked={picked.has(d.id)}
                  onChange={() => setPicked(prev => {
                    const next = new Set(prev);
                    next.has(d.id) ? next.delete(d.id) : next.add(d.id);
                    return next;
                  })}
                  style={{ cursor: 'pointer', flexShrink: 0 }}
                />
              )}
              <span style={{ opacity: scanResult.dryRun && !picked.has(d.id) ? 0.45 : 1 }}>
                <strong style={{ color: 'var(--neutral-800)' }}>"{d.name}"</strong> →{' '}
                <strong style={{ color: 'var(--neutral-800)' }}>"{d.into}"</strong>{' '}
                <span style={{ color: '#16a34a', fontWeight: 600 }}>{Math.round(d.score * 100)}%</span>
                {d.moved != null && <span style={{ color: 'var(--neutral-400)' }}> · {d.moved} tag{d.moved === 1 ? '' : 's'} moved</span>}
              </span>
            </label>
          ))}
          {scanResult.dryRun && scanResult.merged > picked.size && (
            <div style={{ fontSize: 11.5, color: 'var(--neutral-500)', marginTop: 'var(--space-6)', fontStyle: 'italic' }}>
              {scanResult.merged - picked.size} unticked, left as they are, and offered again next scan.
            </div>
          )}
          {scanResult.decisions.filter(d => d.verdict === 'failed').map(d => (
            <div key={`f${d.id}`} style={{ fontSize: 12.5, marginBottom: 5, color: 'var(--feedback-error)' }}>
              "{d.name}", {d.reason}
            </div>
          ))}
        </div>
      )}

      {/* Filter by kind. Only shown when there is more than one kind waiting —
          a row of chips over a queue of nothing but tropes is noise. */}
      {Object.keys(kindCounts).length > 1 && (
        <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap', marginBottom: 'var(--space-14)' }}>
          {[['all', items.length], ...Object.entries(kindCounts).sort((a, b) => b[1] - a[1])].map(([k, n]) => (
            <button
              key={k}
              onClick={() => setKind(k)}
              style={{
                fontSize: 'var(--font-size-xs)', fontWeight: 600, padding: '4px 11px',
                borderRadius: 'var(--radius-xxl)', cursor: 'pointer', fontFamily: 'var(--font-body)',
                background: kind === k ? 'var(--neutral-800)' : 'var(--brand-white)',
                color:      kind === k ? 'var(--brand-white)' : 'var(--neutral-500)',
                border:     kind === k ? '1px solid var(--neutral-800)' : '1px solid var(--brand-sand)',
              }}
            >
              {k === 'all' ? 'everything' : k} {n}
            </button>
          ))}
        </div>
      )}

      {visibleItems.map((item, idx) => {
        const isMergeFlag = item.merge_status === 'pending_review';
        const busy = working === item.id;
        return (
          <div key={item.id} style={{
            padding: isMergeFlag ? '16px' : '14px 0',
            margin: isMergeFlag ? (isMobile ? '4px -14px' : '4px -22px') : 0,
            borderBottom: idx < items.length - 1 ? '1px solid var(--brand-cream)' : 'none',
            background: isMergeFlag ? '#fdf9ff' : 'transparent',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-14)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-xs)', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--neutral-800)' }}>
                    "{item.canonical_name}"
                  </span>
                  {isMergeFlag
                    ? <Pill color="merge">merge flag</Pill>
                    : <Pill color={categoryPill(item.category).color}>new {categoryPill(item.category).label}</Pill>}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--neutral-500)' }}>
                  {timeAgo(item.created_at)}
                  {isMergeFlag && item.suggested_trope && (
                    <div style={{ marginTop: 'var(--space-10)', padding: '10px 14px', background: 'var(--brand-white)', border: '1px solid #e9d5ff', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-sm)', flexWrap: 'wrap', gap: 'var(--space-6)' }}>
                        <div style={{ fontWeight: 700, color: '#6b21a8' }}>Merge preview</div>
                        <button
                          onClick={() => setSwappedItems(prev => {
                            const next = new Set(prev);
                            next.has(item.id) ? next.delete(item.id) : next.add(item.id);
                            return next;
                          })}
                          style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, padding: '3px 10px', borderRadius: 'var(--radius-md)', cursor: 'pointer', border: '1px solid #e9d5ff', background: '#fdf4ff', color: '#6b21a8', fontFamily: 'var(--font-body)' }}
                        >
                          ⇄ Flip direction
                        </button>
                      </div>
                      {swappedItems.has(item.id) ? (
                        <div style={{ color: 'var(--neutral-500)', lineHeight: 1.6 }}>
                          <strong style={{ color: 'var(--neutral-800)' }}>"{item.suggested_trope.canonical_name}"</strong>
                          {' '}will merge into{' '}
                          <strong style={{ color: 'var(--neutral-800)' }}>"{item.canonical_name}"</strong> (kept as canonical).
                        </div>
                      ) : (
                        <div style={{ color: 'var(--neutral-500)', lineHeight: 1.6 }}>
                          {item.affected_book_count > 0
                            ? <><strong style={{ color: 'var(--neutral-800)' }}>{item.affected_book_count} books</strong> tagged "{item.canonical_name}" will be retagged as </>
                            : <>No books tagged yet. After merge, "{item.canonical_name}" maps to </>}
                          <strong style={{ color: 'var(--neutral-800)' }}>"{item.suggested_trope.canonical_name}"</strong>.
                        </div>
                      )}
                      {/* Future tense, and no tick. This sits under a heading
                          that says "Merge preview" and describes what Merge
                          would do — but it was written as "✓ Saved as alias",
                          which reads as a confirmation that it already
                          happened. Nothing has been saved until the button is
                          pressed. */}
                      <div style={{ color: '#9333ea', fontSize: 11.5, marginTop: 5, fontWeight: 600 }}>If you merge: saved as an alias, and it won't come back to this queue.</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions — stack vertically on mobile */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 'var(--space-sm)', flexShrink: 0,
              }}>
                {isMergeFlag && item.suggested_trope && <SimBar score={item.similarity} />}
                <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: isMobile ? 'wrap' : 'nowrap', justifyContent: 'flex-end' }}>
                  {isMergeFlag && item.suggested_trope ? (
                    <>
                      <Btn variant="merge" onClick={() => handleMerge(item, swappedItems.has(item.id))} disabled={busy}>
                        {swappedItems.has(item.id) ? '← Merge' : 'Merge →'}
                      </Btn>
                      <Btn variant="approve" onClick={() => handleKeepBoth(item)} disabled={busy}>Keep both</Btn>
                    </>
                  ) : (
                    <>
                      <Btn variant="approve" onClick={() => handleApprove(item)} disabled={busy}>Approve</Btn>
                      <Btn variant="reject"  onClick={() => handleReject(item)}  disabled={busy}>Reject</Btn>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TAB 4 — DUPLICATE REVIEW
// ══════════════════════════════════════════════════════════════
function DuplicateReview({ onCountChange, isMobile }) {
  const [pairs, setPairs]           = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [working, setWorking]       = useState(null);
  const [category, setCategory]     = useState('all');
  const [categories, setCategories] = useState([]);
  const [progress, setProgress]     = useState({ done: 0, total: 0 });
  const onCountChangeRef = useRef(onCountChange);
  useEffect(() => { onCountChangeRef.current = onCountChange; }, [onCountChange]);

  const load = useCallback(async () => {
    setLoading(true);
    // One definition of "a duplicate pair" for the whole panel (loadDuplicatePairs):
    // the sidebar badge, the Overview card and this tab all count its result.
    // A refused read comes back ok: false, so "0 pairs" here can be trusted as an
    // answer rather than an absence of one.
    const read = await loadDuplicatePairs();
    if (!read.ok) {
      setLoadError(read.error || 'Could not load the flagged pairs.');
      setPairs([]); setLoading(false);
      return;
    }
    setLoadError(null);
    const active = read.pairs;
    const cats = [...new Set(active.map(p => p.tropeA?.category).filter(Boolean))].sort();
    setCategories(cats);
    setPairs(active);
    onCountChangeRef.current?.(active.length);
    // 'done' was logs.length - active.length, which counted pairs that had been
    // filtered out for ANY reason — a deleted trope, a merge done elsewhere —
    // and called them resolved. Ask for the reviewed ones instead; Keep both
    // relabels rows kept_both, so those count too.
    const { count: reviewedCount } = await supabase
      .from('trope_merge_log')
      .select('*', { count: 'exact', head: true })
      .in('action', ['flagged_review', 'kept_both']).not('reviewed_at', 'is', null);
    setProgress({ done: reviewedCount || 0, total: (reviewedCount || 0) + active.length });
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = category === 'all' ? pairs : pairs.filter(p => p.tropeA?.category === category);

  async function handleKeepBoth(pair) {
    setWorking(pair.log.id);
    try {
      const A = pair.log.original_trope_id;
      const B = pair.log.merged_into_id;
      // pending_review only, so a rejected or merged tag stays off. Tag first, so a failure leaves the pair on screen to retry.
      const { error: tagErr } = await supabase.from('tropes')
        .update({ is_active: true, merge_status: 'active', merged_into: null })
        .in('id', [A, B]).eq('merge_status', 'pending_review');
      if (tagErr) throw new Error('Could not switch the waiting tag on: ' + tagErr.message);
      const now = new Date().toISOString();
      const [r1, r2] = await Promise.all([
        // Scoped to the unreviewed flagged rows. Without the action/reviewed_at
        // filters this also rewrote any auto_merged or admin_merge row for the
        // same pair, editing settled history to say 'kept_both'.
        supabase.from('trope_merge_log').update({ reviewed_at: now, action: 'kept_both' })
          .eq('original_trope_id', A).eq('merged_into_id', B)
          .eq('action', 'flagged_review').is('reviewed_at', null),
        supabase.from('trope_merge_log').update({ reviewed_at: now, action: 'kept_both' })
          .eq('original_trope_id', B).eq('merged_into_id', A)
          .eq('action', 'flagged_review').is('reviewed_at', null),
      ]);
      if (r1.error) throw new Error('Direction A→B: ' + r1.error.message);
      if (r2.error) throw new Error('Direction B→A: ' + r2.error.message);
      await logAudit('update', 'trope', `Kept both: "${pair.tropeA?.canonical_name}" and "${pair.tropeB?.canonical_name}"`);
      await load();
    } catch (err) { alert('Keep both failed: ' + err.message); }
    finally { setWorking(null); }
  }

  async function handleMerge(pair, keepId, aliasId, keepName, aliasName) {
    setWorking(pair.log.id);
    if (!keepId || !aliasId) { alert('No merge target'); setWorking(null); return; }
    try {
      const { data: pairNow, error: pairErr } = await supabase.from('tropes')
        .select('id, canonical_name, merge_status').in('id', [keepId, aliasId]);
      if (pairErr) { alert(`Merge failed, nothing was changed: ${pairErr.message}`); return; }
      const gone = (pairNow || []).find(t => t.merge_status === 'merged' || t.merge_status === 'rejected');
      if (gone || (pairNow || []).length !== 2) {
        alert(`Nothing was merged: "${gone?.canonical_name ?? 'one of these tags'}" was ${gone?.merge_status ?? 'deleted'} after this was flagged.`);
        return;
      }
      // Same transaction as the pending-review queue above. This panel had its
      // own copy of the old four-write version — the colliding
      // `update book_tags set trope_id = keep where trope_id = alias`, with no
      // `error` destructured on any write, so the merge reported success while
      // every book kept a tag pointing at a dead trope.
      const { data: result, error } = await supabase.rpc('merge_tropes', {
        p_alias_id:   aliasId,
        p_keep_id:    keepId,
        p_alias_name: aliasName,
        p_keep_name:  keepName,
        p_similarity: pair.log.similarity_score || 0,
      });
      if (error) { alert(`Merge failed, nothing was changed: ${error.message}`); return; }

      // Not part of merge_tropes, and specific to this panel: these pairs come
      // from process-trope, which may have left the keeper inactive or
      // pending_review. Reactivating it is a single-row update that cannot
      // collide, so it is safe outside the transaction — but it still has to
      // be checked, which is what the old version never did.
      const { error: keepErr } = await supabase.from('tropes')
        .update({ is_active: true, merge_status: 'active', merged_into: null })
        .eq('id', keepId);
      if (keepErr) {
        alert(`Tags merged, but "${keepName}" could not be reactivated: ${keepErr.message}`);
      }

      // merge_tropes inserts a fresh admin_merge row; it does not resolve the
      // flagged_review row that put this pair on screen. Left alone that row
      // stays reviewed_at null for ever, so the pair counts as neither
      // outstanding nor resolved.
      const { error: logErr } = await supabase.from('trope_merge_log')
        .update({ reviewed_at: new Date().toISOString() })
        .eq('id', pair.log.id).is('reviewed_at', null);
      if (logErr) console.error('could not mark the flagged pair reviewed:', logErr.message);

      await logAudit(
        'merge', 'trope',
        `Merged "${aliasName}" → "${keepName}" (auto-approved; ${result?.moved ?? 0} moved, ${result?.deduped ?? 0} already tagged)`,
        aliasId,
      );
      load();
    } catch (err) { alert('Merge failed: ' + err.message); }
    finally { setWorking(null); }
  }

  if (loading) return <div style={{ padding: 'var(--space-xxl)', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading pairs...</div>;

  return (
    <div>
      {/* Progress bar */}
      <div style={{
        padding: '10px 14px', background: 'var(--brand-paper)', border: '1px solid var(--brand-cream)',
        borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-lg)', display: 'flex', alignItems: 'center', gap: 'var(--space-md)',
      }}>
        <div style={{ flex: 1 }}>
          {loadError && (
            <div style={{ fontSize: 11.5, color: 'var(--feedback-error)', fontWeight: 600, marginBottom: 5 }}>
              Could not load duplicate pairs, this is a failed read, not an empty queue. {loadError}
            </div>
          )}
          <div style={{ fontSize: 11.5, color: 'var(--neutral-500)', marginBottom: 5 }}>
            <strong style={{ color: 'var(--neutral-800)' }}>{filtered.length}</strong> pairs to review
            {category !== 'all' && <> in <strong style={{ color: 'var(--neutral-800)' }}>{category}</strong></>}
            {' · '}
            <span style={{ color: '#16a34a', fontWeight: 600 }}>{progress.done} resolved</span>
            {' of '}
            <strong>{progress.total}</strong> total
          </div>
          <div style={{ height: 5, background: 'var(--brand-sand)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 3, background: '#16a34a',
              width: progress.total > 0 ? `${(progress.done / progress.total) * 100}%` : '0%',
              transition: 'width 0.4s ease',
            }} />
          </div>
        </div>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', flexShrink: 0 }}>
          {progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0}%
        </span>
      </div>

      {/* Category filter */}
      <div style={{ display: 'flex', gap: 'var(--space-6)', marginBottom: 'var(--space-lg)', flexWrap: 'wrap' }}>
        <button onClick={() => setCategory('all')} style={{
          fontSize: 11.5, fontWeight: 600, padding: '4px 12px', borderRadius: 14,
          cursor: 'pointer', fontFamily: 'var(--font-body)',
          background: category === 'all' ? 'var(--neutral-800)' : 'var(--brand-white)',
          color: category === 'all' ? 'var(--brand-white)' : 'var(--neutral-500)',
          border: category === 'all' ? '1px solid var(--neutral-800)' : '1px solid var(--brand-sand)',
        }}>
          All ({pairs.length})
        </button>
        {categories.map(cat => {
          const count = pairs.filter(p => p.tropeA?.category === cat).length;
          if (!count) return null;
          return (
            <button key={cat} onClick={() => setCategory(cat)} style={{
              fontSize: 11.5, fontWeight: 600, padding: '4px 12px', borderRadius: 14,
              cursor: 'pointer', fontFamily: 'var(--font-body)',
              background: category === cat ? 'var(--neutral-800)' : 'var(--brand-white)',
              color: category === cat ? 'var(--brand-white)' : 'var(--neutral-500)',
              border: category === cat ? '1px solid var(--neutral-800)' : '1px solid var(--brand-sand)',
            }}>
              {cat} ({count})
            </button>
          );
        })}
      </div>

      {!filtered.length ? (
        <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>
          ✓ All pairs in this category reviewed
        </div>
      ) : filtered.map((pair, idx) => {
        const { log, tropeA, tropeB } = pair;
        const busy = working === log.id;
        const last = idx === filtered.length - 1;
        const suggestKeep = (tropeB?.usage_count || 0) > (tropeA?.usage_count || 0) ? 'B' : 'A';

        return (
          <div key={log.id} style={{ padding: '16px 0', borderBottom: last ? 'none' : '1px solid var(--brand-cream)' }}>
            {/* Pair cards — stack on mobile */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr auto 1fr',
              gap: 'var(--space-md)', alignItems: 'center', marginBottom: 'var(--space-md)',
            }}>
              {/* Trope A */}
              <div style={{
                padding: '12px 14px', borderRadius: 'var(--radius-10)',
                border: suggestKeep === 'A' ? '2px solid var(--feedback-warning)' : '1px solid var(--brand-sand)',
                background: suggestKeep === 'A' ? '#fffbeb' : 'var(--brand-paper)',
                position: 'relative',
              }}>
                {suggestKeep === 'A' && (
                  <div style={{
                    position: 'absolute', top: -8, left: 12,
                    fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: 'var(--feedback-warning-ink)',
                    background: 'var(--feedback-warning-bg)', border: '1px solid var(--feedback-warning-border)',
                    padding: '1px 7px', borderRadius: 'var(--radius-md)', letterSpacing: 0.5,
                  }}>SUGGESTED CANONICAL</div>
                )}
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--neutral-800)', marginBottom: 'var(--space-xs)' }}>
                  {tropeA?.canonical_name}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>
                  {tropeA?.category} · {tropeA?.usage_count || 0} books
                </div>
                {!busy && (
                  <div style={{ display: 'flex', gap: 5, marginTop: 'var(--space-10)' }}>
                    <Btn small variant="primary"
                      onClick={() => handleMerge(pair, tropeA.id, tropeB.id, tropeA.canonical_name, tropeB.canonical_name)}
                      disabled={busy}>
                      Keep this →
                    </Btn>
                  </div>
                )}
              </div>

              {/* Middle similarity — show inline on mobile */}
              <div style={{ textAlign: 'center', flexShrink: 0 }}>
                <SimBar score={log.similarity_score} />
                <div style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', marginTop: 'var(--space-xs)' }}>similarity</div>
              </div>

              {/* Trope B */}
              <div style={{
                padding: '12px 14px', borderRadius: 'var(--radius-10)',
                border: suggestKeep === 'B' ? '2px solid var(--feedback-warning)' : '1px solid var(--brand-sand)',
                background: suggestKeep === 'B' ? '#fffbeb' : 'var(--brand-paper)',
                position: 'relative',
              }}>
                {suggestKeep === 'B' && (
                  <div style={{
                    position: 'absolute', top: -8, left: 12,
                    fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: 'var(--feedback-warning-ink)',
                    background: 'var(--feedback-warning-bg)', border: '1px solid var(--feedback-warning-border)',
                    padding: '1px 7px', borderRadius: 'var(--radius-md)', letterSpacing: 0.5,
                  }}>SUGGESTED CANONICAL</div>
                )}
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--neutral-800)', marginBottom: 'var(--space-xs)' }}>
                  {tropeB?.canonical_name}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>
                  {tropeB?.category} · {tropeB?.usage_count || 0} books
                </div>
                {!busy && (
                  <div style={{ display: 'flex', gap: 5, marginTop: 'var(--space-10)' }}>
                    <Btn small variant="primary"
                      onClick={() => handleMerge(pair, tropeB.id, tropeA.id, tropeB.canonical_name, tropeA.canonical_name)}
                      disabled={busy}>
                      Keep this →
                    </Btn>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom row */}
            <div style={{
              display: 'flex', alignItems: isMobile ? 'flex-start' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'flex-end', gap: 'var(--space-sm)',
            }}>
              <span style={{ fontSize: 11.5, color: 'var(--neutral-400)' }}>
                Click "Keep this →" on the trope you want as canonical. The other becomes an alias.
              </span>
              <Btn small variant="amber" onClick={() => handleKeepBoth(pair)} disabled={busy}>
                These are different, keep both
              </Btn>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TAB 2 — ALIAS EDITOR
// ══════════════════════════════════════════════════════════════
function AliasEditor() {
  const [groups, setGroups]     = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [adding, setAdding]     = useState(null);
  const [newAlias, setNewAlias] = useState('');
  const [saving, setSaving]     = useState(false);

  async function load() {
    setLoading(true);
    // Same trap as the Duplicates tab: fetchAllRows returns [] on failure, so
    // "No aliases yet." was indistinguishable from a query that never ran.
    const { count: aliasCount, error: countErr } = await supabase
      .from('trope_aliases').select('*', { count: 'exact', head: true });
    if (countErr) { setLoadError(countErr.message); setGroups([]); setLoading(false); return; }

    const aliases = await fetchAllRows(() => supabase
      .from('trope_aliases')
      .select('id, alias, created_at, trope:tropes(id, canonical_name, usage_count)'));
    if (aliasCount > 0 && aliases.length === 0) {
      setLoadError(`${aliasCount} alias(es) exist but none could be loaded.`);
      setGroups([]); setLoading(false); return;
    }
    // An alias whose trope_id is null joins to nothing and used to be dropped
    // silently — invisible here while still redirecting readers.
    const orphaned = (aliases || []).filter(a => !a.trope).length;
    setLoadError(orphaned > 0
      ? `${orphaned} alias(es) are not attached to any trope and cannot be managed here.`
      : null);
    aliases.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const map = {};
    (aliases || []).forEach(a => {
      if (!a.trope) return;
      const tid = a.trope.id;
      if (!map[tid]) map[tid] = { trope: a.trope, aliases: [] };
      map[tid].aliases.push(a);
    });
    setGroups(Object.values(map).sort((a, b) => (b.trope.usage_count || 0) - (a.trope.usage_count || 0)));
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function removeAlias(aliasId, aliasText) {
    // The delete used to go unchecked, and the audit line was written either
    // way — so an RLS refusal left the alias in place while the log recorded a
    // removal that never happened.
    const { error } = await supabase.from('trope_aliases').delete().eq('id', aliasId);
    if (error) { alert(`Could not remove "${aliasText}": ${error.message}`); return; }
    await logAudit('delete', 'trope', `Removed alias "${aliasText}"`);
    load();
  }

  async function addAlias(tropeId, tropeName) {
    if (!newAlias.trim() || saving) return;
    setSaving(true);
    // merge_tropes inserts lower(alias), and trope_aliases_alias_idx is unique
    // on lower(alias) — store it the same way rather than by whatever was typed.
    const { error } = await supabase.from('trope_aliases').insert({ trope_id: tropeId, alias: newAlias.trim().toLowerCase() });
    if (error) { alert(error.message.includes('duplicate') ? 'That alias already exists.' : error.message); setSaving(false); return; }
    await logAudit('create', 'trope', `Added alias "${newAlias.trim()}" → "${tropeName}"`);
    setAdding(null); setNewAlias(''); setSaving(false); load();
  }

  if (loading) return <div style={{ padding: 'var(--space-xxl)', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading aliases...</div>;

  return (
    <div>
      {loadError && (
        <div style={{ fontSize: 12, color: 'var(--feedback-error)', fontWeight: 600, marginBottom: 'var(--space-10)' }}>
          {loadError}
        </div>
      )}
      <p style={{ fontSize: 12.5, color: 'var(--neutral-500)', marginBottom: 'var(--space-lg)', lineHeight: 1.6 }}>
        Aliases are created automatically on every merge. When a reader types a trope,
        aliases are checked first, matches silently redirect to the canonical.
      </p>
      {!groups.length ? (
        <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic', background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', border: '1px dashed var(--brand-sand)' }}>
          No aliases yet.
        </div>
      ) : groups.map(g => (
        <div key={g.trope.id} style={{ background: 'var(--brand-paper)', border: '1px solid var(--brand-cream)', borderRadius: 'var(--radius-10)', padding: '12px 14px', marginBottom: 'var(--space-10)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-10)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--neutral-800)' }}>{g.trope.canonical_name}</span>
            <span style={{ fontSize: 10.5, color: 'var(--neutral-400)' }}>
              · {g.trope.usage_count || 0} books · {g.aliases.length} alias{g.aliases.length !== 1 ? 'es' : ''}
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)' }}>
            {g.aliases.map(a => (
              <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 500, padding: '4px 10px', borderRadius: 14, background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' }}>
                {a.alias}
                <span onClick={() => removeAlias(a.id, a.alias)}
                  style={{ color: 'var(--neutral-400)', cursor: 'pointer', fontSize: 'var(--font-size-sm)', fontWeight: 700 }}
                  onMouseEnter={e => e.target.style.color = '#dc2626'}
                  onMouseLeave={e => e.target.style.color = '#a8a29e'}>×</span>
              </span>
            ))}
            {adding === g.trope.id ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <input autoFocus value={newAlias} onChange={e => setNewAlias(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') addAlias(g.trope.id, g.trope.canonical_name);
                    if (e.key === 'Escape') { setAdding(null); setNewAlias(''); }
                  }}
                  placeholder="Type alias..."
                  style={{ fontSize: 11.5, padding: '4px 9px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--feedback-warning)', outline: 'none', width: 130, fontFamily: 'var(--font-body)' }}
                />
                <span onClick={() => addAlias(g.trope.id, g.trope.canonical_name)} style={{ fontSize: 11.5, color: 'var(--feedback-warning)', cursor: 'pointer', fontWeight: 700 }}>✓</span>
                <span onClick={() => { setAdding(null); setNewAlias(''); }} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', cursor: 'pointer', fontWeight: 700 }}>×</span>
              </span>
            ) : (
              <span onClick={() => { setAdding(g.trope.id); setNewAlias(''); }}
                style={{ fontSize: 11.5, color: 'var(--neutral-500)', cursor: 'pointer', padding: '4px 10px', borderRadius: 14, border: '1px dashed var(--neutral-300)' }}
                onMouseEnter={e => { e.target.style.borderColor = '#d97706'; e.target.style.color = '#d97706'; }}
                onMouseLeave={e => { e.target.style.borderColor = '#d6d3d1'; e.target.style.color = '#78716c'; }}>
                + Add alias
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TAB 3 — MERGE LOG
// ══════════════════════════════════════════════════════════════
function MergeLog() {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all');

  useEffect(() => {
   supabase.from('trope_merge_log').select('*')
      .not('action', 'eq', 'flagged_review')
      .order('created_at', { ascending: false })
      .limit(100)
      .then(({ data }) => { setLogs(data || []); setLoading(false); })
      .catch(err => { console.error('MergeLog fetch failed:', err.message); setLoading(false); });
  }, []);

  const ACTION_META = {
    auto_merged:    { label: 'auto-merged',  color: 'green' },
    admin_merge:    { label: 'admin merge',  color: 'merge' },
    kept_both:      { label: 'kept both',    color: 'amber' },
    admin_approved: { label: 'approved',     color: 'green' },
    admin_rejected: { label: 'rejected',     color: 'red'   },
    flagged_review: { label: 'flagged',      color: 'amber' },
    new_trope:      { label: 'new trope',    color: 'gray'  },
    // process-trope writes these when a near-spelling comes in (ticket 72).
    // A tag still in the queue is renamed: "old" -> "new", green like an auto-merge.
    // A tag already approved is left alone and the better spelling only noticed.
    spelling_corrected: { label: 'spelling fixed',     color: 'green' },
    spelling_suggested: { label: 'spelling suggested', color: 'amber' },
  };

  const FILTERS = [
    { id: 'all',         label: 'All' },
    { id: 'auto_merged', label: 'Auto-merged' },
    { id: 'admin_merge', label: 'Admin merge' },
    { id: 'kept_both',   label: 'Kept both' },
  ];

  const filtered = filter === 'all' ? logs : logs.filter(l => l.action === filter);

  if (loading) return <div style={{ padding: 'var(--space-xxl)', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading log...</div>;

  return (
    <div>
      <div style={{ display: 'flex', gap: 'var(--space-6)', marginBottom: 'var(--space-lg)', flexWrap: 'wrap', alignItems: 'center' }}>
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} style={{
            fontSize: 11.5, fontWeight: 600, padding: '4px 12px', borderRadius: 14,
            cursor: 'pointer', fontFamily: 'var(--font-body)',
            background: filter === f.id ? 'var(--neutral-800)' : 'var(--brand-white)',
            color:      filter === f.id ? 'var(--brand-white)'    : 'var(--neutral-500)',
            border:     filter === f.id ? '1px solid var(--neutral-800)' : '1px solid var(--brand-sand)',
          }}>{f.label}</button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--neutral-400)' }}>{filtered.length} entries</span>
      </div>
      {!filtered.length ? (
        <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>No entries.</div>
      ) : filtered.map((log, idx) => {
        const meta   = ACTION_META[log.action] || { label: log.action, color: 'gray' };
        const isKept = log.action === 'kept_both';
        const isAuto = log.action === 'auto_merged';
        // A suggestion changes no name, so the log row carries the approved name on
        // both sides. Print it once instead of "X -> X".
        const sameName = log.action === 'spelling_suggested' && log.original_name === log.merged_into_name;
        const pct    = Math.round((log.similarity_score || 0) * 100);
        return (
          <div key={log.id} style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap',
            padding: '10px 0', borderBottom: idx < filtered.length - 1 ? '1px solid var(--brand-cream)' : 'none',
            fontSize: 12.5, color: 'var(--neutral-500)',
          }}>
            {isAuto && <span style={{ fontSize: 'var(--font-size-xs)' }}>🤖</span>}
            <strong style={{ color: 'var(--neutral-800)' }}>"{log.original_name}"</strong>
            {!sameName && <span style={{ color: 'var(--neutral-300)', fontSize: 'var(--font-size-lg)' }}>{isKept ? '·' : '→'}</span>}
            {sameName ? null : isKept
              ? <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: '#16a34a' }}>kept separate from "{log.merged_into_name}"</span>
              : log.merged_into_name ? <strong style={{ color: 'var(--neutral-800)' }}>"{log.merged_into_name}"</strong> : null}
            {/* The `pct > 0` guard is load-bearing, not cosmetic. similarity_score
                is NOT NULL and the merge RPCs write coalesce(p_similarity, 0),
                so a 0 in this table means "never measured" — showing it as "0%"
                would claim the two names are unrelated. Do not simplify this to
                always print the percentage. */}
            <Pill color={meta.color}>{pct > 0 ? `${pct}% · ` : ''}{meta.label}</Pill>
            <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--neutral-400)' }}>{timeAgo(log.created_at)}</span>
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN EXPORT
// ══════════════════════════════════════════════════════════════
export default function TropeQueueSection({ onBadgeChange, isMobile }) {
  const [tab, setTab]               = useState('queue');
  // null = not known yet. The badge is reported only once BOTH are known, so it is
  // never pending tropes alone: the screen opens on the Pending tab, and the
  // duplicate pairs used to be counted only once the Duplicates tab mounted.
  const [pendingN, setPendingN]     = useState(null);
  const [dupN, setDupN]             = useState(null);
  const [mergeableCount, setMergeableCount] = useState(0);
  const queueCount = pendingN ?? 0;
  const dupCount   = dupN ?? 0;
  const handleQueueCount = useCallback((n, mergeable = 0) => {
    setPendingN(n);
    setMergeableCount(mergeable);
  }, []);
  const handleDupCount = useCallback((n) => { setDupN(n); }, []);

  // The duplicate pairs are counted when the screen opens, the same way the
  // Duplicates tab and the sidebar badge count them (loadDuplicatePairs). If that
  // read fails the count stays unknown and the badge is left as the portal set it.
  useEffect(() => {
    let off = false;
    loadDuplicatePairs().then(r => { if (!off && r.ok) setDupN(r.pairs.length); });
    return () => { off = true; };
  }, []);

  const onBadgeChangeRef = useRef(onBadgeChange);
  useEffect(() => { onBadgeChangeRef.current = onBadgeChange; }, [onBadgeChange]);
  useEffect(() => {
    if (pendingN === null || dupN === null) return;
    onBadgeChangeRef.current?.('tropes', pendingN + dupN);
  }, [pendingN, dupN]);

  const totalBadge = queueCount + dupCount;

  const TABS = [
    { id: 'queue',   label: `Pending${queueCount > 0 ? ` (${queueCount})` : ''}` },
    { id: 'dupes',   label: `Duplicates${dupCount > 0 ? ` (${dupCount})` : ''}`, highlight: dupCount > 0 },
    { id: 'aliases', label: 'Aliases' },
    { id: 'log',     label: 'Log' },
  ];

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '30px 36px', fontFamily: 'var(--font-body)' }}>
      <div style={{ marginBottom: 26 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 20 : 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6, lineHeight: 1.1, margin: 'var(--space-none)' }}>
          Trope queue
        </h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', marginTop: 'var(--space-xs)', lineHeight: 1.6 }}>
          {isMobile ? (
            <>Approve, reject, or merge submitted tropes.</>
          ) : (
            <>
              Approve, reject, or merge submitted tropes ·{' '}
              <span style={{ color: '#16a34a', fontWeight: 600 }}>≥90% auto-merges</span> ·{' '}
              <span style={{ color: '#9333ea', fontWeight: 600 }}>75–89% flagged for review</span> ·{' '}
              <span style={{ color: 'var(--neutral-500)', fontWeight: 600 }}>&lt;75% new canonical</span>
            </>
          )}
          {totalBadge > 0 && (
            <span style={{ marginLeft: 'var(--space-10)', background: 'var(--feedback-error)', color: 'var(--brand-white)', fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-10)' }}>
              {totalBadge} need attention
            </span>
          )}
          {totalBadge > 0 && (
            <span style={{ marginLeft: 'var(--space-6)' }}>
              <InfoTip width={320} text={
                `Pending (${queueCount}) + Duplicates (${dupCount}).\n\n`
                + `PENDING, every trope a reader or author submitted that you have not approved or rejected yet. None of these are live on the site.\n\n`
                + `${mergeableCount} of them look like a duplicate of a trope you already have, so they offer Merge instead of Approve. They sort to the top.\n\n`
                + `DUPLICATES, pairs of tropes flagged as possibly the same thing.\n\n`
                + `The Tropes page shows ${mergeableCount} because it counts only the possible-duplicates, not the whole pending list.`
              } />
            </span>
          )}
        </p>
      </div>

      <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? '14px' : '20px 22px' }}>
        {/* Tabs — scrollable on mobile */}
        <div style={{
          display: 'flex', borderBottom: '2px solid var(--brand-sand)',
          marginBottom: 18, marginTop: -4,
          overflowX: 'auto', WebkitOverflowScrolling: 'touch',
        }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              fontSize: 'var(--font-size-sm)', fontWeight: 600,
              padding: isMobile ? '8px 12px' : '8px 16px',
              color: tab === t.id ? 'var(--neutral-800)' : t.highlight ? '#9333ea' : 'var(--neutral-400)',
              border: 'none', background: 'transparent',
              borderBottom: tab === t.id ? '2px solid var(--neutral-800)' : '2px solid transparent',
              marginBottom: -2, cursor: 'pointer', fontFamily: 'var(--font-body)',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'queue'   && <PendingQueue    onCountChange={handleQueueCount} isMobile={isMobile} />}
        {tab === 'dupes'   && <DuplicateReview onCountChange={handleDupCount}   isMobile={isMobile} />}
        {tab === 'aliases' && <AliasEditor />}
        {tab === 'log'     && <MergeLog />}
      </div>
    </div>
  );
}